import { useState } from "react";
import { Check, ShieldCheck, FileText, Calculator, ClipboardList, NotebookPen, BookOpen, GraduationCap, MessagesSquare } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { PageHero, SectionHeading, DisclaimerBar } from "../components/shared/Primitives";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const FEATURES = [
  { icon: FileText, title: "Professional templates", body: "Over 100 templates for everything you need — and everything you didn't know you would need." },
  { icon: ClipboardList, title: "Shift Toolkit", body: "Arrival, logs, medication, handover, incident, end-of-shift and more — ready to print or send." },
  { icon: ShieldCheck, title: "Payment protection", body: "Late-payment letters, chaser templates and deposit policies that stand up — plus script suggestions to help you handle awkward payment conversations professionally." },
  { icon: Calculator, title: "Tax Toolkit + calculators", body: "Self-assessment workbook, expense lists and a pricing calculator that covers real costs." },
  { icon: NotebookPen, title: "Safety checklists", body: "Risk-aware practical checklists for new arrangements and ongoing shifts, and guidelines to help you keep your practice safe." },
  { icon: BookOpen, title: "Family agreement templates", body: "Plain-English agreements you can use with any family, in any arrangement." },
  { icon: GraduationCap, title: "CPD tracker + training guidance", body: "Keep track of learning, certifications and the training that opens better work." },
];

export default function ProCarerCircle() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { user } = useAuth();
  const nav = useNavigate();

  const startCheckout = async () => {
    if (!user) { nav("/register?next=/pro-carer-circle"); return; }
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/api/payments/checkout", {
        package_id: "pro_carer_monthly",
        origin_url: window.location.origin,
        user_email: user.email,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-pro-carer-circle">
      <PageHero
        eyebrow="Pro Carer Office"
        title="Build your independent care business with confidence"
        description="Pro Carer Office is your practical member workspace for running independent care professionally. Access ready-to-use tools, editable templates, clear guidance and growing business resources designed to help you organise placements, communicate with confidence, protect your time and build a more sustainable way of working."
        accent="purple"
        bgTint="carer"
      >
        <p
          className="w-full text-base md:text-lg font-semibold text-ink"
          data-testid="pro-price-line"
        >
          £19 per month. Start with a 7-day free trial.
        </p>
        <div className="w-full flex flex-wrap gap-3">
          <a
            href="/membership/pro-carer-circle"
            className="btn-purple"
            data-testid="pro-cta-start"
          >
            Start your 7-day free trial
          </a>
          <a
            href="#features"
            className="btn-outline"
            data-testid="pro-cta-see-included"
          >
            See what&rsquo;s included
          </a>
        </div>
        <ul
          className="w-full mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm text-ink-muted"
          data-testid="pro-value-strip"
        >
          {[
            "Professional templates and practical records",
            "Shift Toolkit and care-work systems",
            "Pricing, planning and business guidance",
            "A growing resource library for independent carers",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2.5">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-purple shrink-0" aria-hidden="true"></span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </PageHero>

      <section id="features" className="section">
        <div className="container-jcc">
          <SectionHeading eyebrow="What's included" accent="purple" title="Everything you need. Nothing you don't." />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="card-base p-6" data-testid={`pro-feature-${f.title.toLowerCase().split(' ').slice(0,2).join('-')}`}>
                <div className="w-11 h-11 rounded-xl bg-purple-50 text-purple flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-display font-semibold text-lg">{f.title}</h3>
                <p className="mt-2 text-ink-muted text-sm leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" data-testid="pro-care-discussion-room-section">
        <div className="container-jcc">
          <div
            className="rounded-3xl border-2 border-purple/60 bg-purple/5 p-8 md:p-12 grid lg:grid-cols-5 gap-8 items-center"
            data-testid="pro-care-discussion-room-card"
          >
            <div className="lg:col-span-3">
              <div className="overline text-purple mb-3">Care Discussion Room / Care Partnership Room</div>
              <h2 className="font-display font-bold text-2xl md:text-3xl leading-tight">
                A private shared space for a self-employed carer and a family, before you commit.
              </h2>
              <p className="mt-4 text-ink-muted leading-relaxed">
                The Care Discussion Room is a private, pre-discussion space where you and a
                prospective family can talk through the arrangement together — expectations,
                schedule, boundaries, payment terms, key documents — before signing anything
                or activating the full Care Partnership Room. Just Care Community is not an
                agency and takes no commission from the arrangement; the room simply gives
                both sides a calm, organised place to reach a shared understanding.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/care-discussion-room"
                  className="btn-purple"
                  data-testid="pro-cta-care-discussion-room"
                >
                  Learn about the Care Discussion Room
                </Link>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="rounded-2xl bg-white border border-purple/30 p-6">
                <div className="w-11 h-11 rounded-xl bg-purple-50 text-purple flex items-center justify-center mb-4">
                  <MessagesSquare className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <ul className="space-y-3 text-sm text-ink-muted leading-relaxed">
                  <li>&bull; Private to you and the prospective family</li>
                  <li>&bull; A pre-discussion place before any commitment</li>
                  <li>&bull; Shared record of what you both agreed</li>
                  <li>&bull; Activate the full Care Partnership Room only when both sides are ready</li>
                  <li>&bull; JCC is not an agency and takes no commission</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-surface-carer">
        <div className="container-jcc grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <SectionHeading eyebrow="Pricing" accent="purple" title="Simple monthly membership. Cancel anytime."
              description="No contracts, no commitments, no surprise renewals. If it doesn't help you, don't pay for it." />
            <div className="mt-6"><DisclaimerBar /></div>
          </div>
          <div className="rounded-3xl border-2 border-purple p-10 bg-white">
            <div className="overline text-purple">Pro Carer Office</div>
            <div className="mt-3 flex items-end gap-1">
              <span className="text-5xl font-bold">£19</span><span className="text-ink-muted mb-1.5">/month</span>
            </div>
            <p className="mt-1 text-sm text-ink-muted">7-day free trial · cancel anytime</p>
            <p className="mt-1 text-xs font-semibold text-purple">Individual carer membership only</p>
            <ul className="mt-6 space-y-3 text-sm">
              {["Shift Toolkit and every template","Tax Toolkit + pricing calculator","Payment protection resources","CPD tracker and training guidance","Independent placement guidance","New templates added monthly"].map((t) => (
                <li key={t} className="flex gap-2"><Check className="w-5 h-5 text-purple shrink-0" />{t}</li>
              ))}
            </ul>
            <a
              href="/membership/pro-carer-circle"
              className="btn-purple mt-8 w-full"
              data-testid="pro-cta-start-2"
            >
              Start 7-day free trial
            </a>
            {error && <p className="mt-3 text-sm text-red-600" data-testid="pro-error">{error}</p>}
            <p className="mt-3 text-xs text-ink-muted">Payment processed securely by Stripe. GBP.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
